import {Component, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {from, of} from 'rxjs';
import {finalize, last, switchMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {DonneesExportFormat} from '../parametre-donnees.types';
import {ParametreDonneesStateService} from '../parametre-donnees-state.service';
import {ParametreDonneesApiService} from '../parametre-donnees-api.service';
import {
  DataExportDestinationDto,
  DataExportMappingWriteDto,
  resolveRefModuleTableDbId
} from '../parametre-donnees-api.models';
import {getParametrageSessionIds} from '../parametre-donnees-session.util';
import {ProductAttributeOption, ProductsService} from '../../../../services/products/products.service';

interface ExportMappingRow {
  /** id côté serveur (data_export_mapping.id), absent pour une ligne non encore enregistrée */
  id?: number;
  colonneFichier: string;
  champCible: string;
  transformation: string;
}

@Component({
  selector: 'app-parametre-donnees-export-tab',
  templateUrl: './parametre-donnees-export-tab.component.html',
  styleUrls: ['./parametre-donnees-export-tab.component.scss']
})
export class ParametreDonneesExportTabComponent implements OnChanges {
  @Input() moduleKey = 'produits';

  @ViewChild('nouveauFormatModalTpl') nouveauFormatModalTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeleteFormatModalTpl') confirmDeleteFormatModalTpl!: TemplateRef<unknown>;

  formats: DonneesExportFormat[] = [];
  selectedFormat: DonneesExportFormat | null = null;

  /** true = grille « Modèle 1 / 2 / … », false = écran détail (mapping, destination, test). */
  afficherListeModeles = true;
  destinations: DataExportDestinationDto[] = [];
  destination: 'download' | 'ftp' | 'api' | 'email' = 'download';

  mapping: ExportMappingRow[] = [{ colonneFichier: '', champCible: '', transformation: '' }];

  testResult = '';
  testing = false;
  mappingLoading = false;
  savingMapping = false;
  deletingRowIndex: number | null = null;

  /** Modal « Nouveau format » */
  nouveauFormatNom = '';
  nouveauFormatType = '';
  creatingFormat = false;

  /** Suppression d’un format (appel DELETE puis rechargement liste). */
  deletingFormatId: string | null = null;
  formatToDelete: DonneesExportFormat | null = null;

  /** GET /api/products/attributes — « Colonne fichier » (module produits uniquement). */
  productAttributeOptions: ProductAttributeOption[] = [];

  constructor(
    private readonly state: ParametreDonneesStateService,
    private readonly api: ParametreDonneesApiService,
    private readonly toastr: ToastrService,
    private readonly productsService: ProductsService,
    private readonly modal: NgbModal
  ) {}

  /** Libellé court pour la valeur par défaut (schéma API). */
  attributeDefaultLabel(value: unknown): string {
    if (value === undefined || value === null) {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  /** ng-select « Format d’export » : compare les objets par id (pas la référence). */
  compareExportFormats(a: DonneesExportFormat | null, b: DonneesExportFormat | null): boolean {
    if (!a || !b) {
      return !a && !b;
    }
    return String(a.id) === String(b.id);
  }

  /** Onglet « produits » / « products » : ng-select alimenté par /api/products/attributes. */
  get isProductsModule(): boolean {
    const k = (this.moduleKey || '').toLowerCase();
    return k === 'produits' || k === 'products';
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['moduleKey']) {
      this.loadLists();
    }
  }

  private loadLists(): void {
    this.formats = this.state.getExportFormats(this.moduleKey);
    this.destinations = this.state.getExportDestinations(this.moduleKey);
    this.afficherListeModeles = true;
    this.selectedFormat = null;
    this.testResult = '';
    this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
    this.loadProductAttributesForModule();
  }

  /** Clic sur une carte modèle : ouvre le détail existant (mapping + destination + test). */
  ouvrirModele(f: DonneesExportFormat): void {
    this.selectedFormat = f;
    this.afficherListeModeles = false;
    this.testResult = '';
    this.loadMappingsForSelected();
  }

  /** Retour à la grille des modèles (sans perdre la liste des formats). */
  retourListeModeles(): void {
    this.afficherListeModeles = true;
  }

  private loadProductAttributesForModule(): void {
    if (!this.isProductsModule) {
      this.productAttributeOptions = [];
      return;
    }
    this.productsService.getProductAttributes().subscribe({
      next: (rows) => {
        this.productAttributeOptions = rows;
      },
      error: () => {
        this.productAttributeOptions = [];
        this.toastr.warning('Impossible de charger les attributs produits (/api/products/attributes).', 'Export');
      }
    });
  }

  onFormatChange(): void {
    this.testResult = '';
    this.loadMappingsForSelected();
  }

  /** Supprime le format actuellement sélectionné (bouton dash à côté du ng-select). */
  supprimerFormatSelectionne(): void {
    const f = this.selectedFormat;
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.executerSuppressionFormat(f);
  }

  /** Supprime un format depuis la grille "Modèles" (bouton sur la carte). */
  supprimerFormatDepuisListe(f: DonneesExportFormat, ev?: Event): void {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.ouvrirPopupConfirmationSuppression(f);
  }

  ouvrirPopupConfirmationSuppression(f: DonneesExportFormat, ev?: Event): void {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.formatToDelete = f;
    this.modal.open(this.confirmDeleteFormatModalTpl, {
      centered: true,
      backdrop: 'static',
      size: 'md'
    });
  }

  confirmerSuppressionDepuisPopup(modal: NgbActiveModal): void {
    const f = this.formatToDelete;
    this.formatToDelete = null;
    modal.close();
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.executerSuppressionFormat(f);
  }

  /** DELETE format puis rechargement de la liste. */
  private executerSuppressionFormat(f: DonneesExportFormat): void {
    if (this.deletingFormatId != null) {
      return;
    }
    const formatId = Number(f.id);
    const { stationId, userId } = getParametrageSessionIds();
    if (!Number.isFinite(formatId) || formatId <= 0 || stationId <= 0 || userId <= 0) {
      this.toastr.error('Session ou format invalide.', 'Export');
      return;
    }
    const prevSelectedId = this.selectedFormat?.id;
    const deletedId = String(f.id);
    this.deletingFormatId = deletedId;
    this.api
      .deleteDataExportFormat(formatId, stationId, userId)
      .pipe(finalize(() => (this.deletingFormatId = null)))
      .subscribe({
        next: () => {
          this.state.reloadExportFormats(this.moduleKey).subscribe(() => {
            this.formats = this.state.getExportFormats(this.moduleKey);
            if (!this.formats.length) {
              this.selectedFormat = null;
              this.afficherListeModeles = true;
              this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
            } else if (String(prevSelectedId) === deletedId) {
              this.selectedFormat = this.formats[0] ?? null;
            } else {
              this.selectedFormat =
                this.formats.find((x) => String(x.id) === String(prevSelectedId)) ?? this.formats[0] ?? null;
            }
            this.testResult = '';
            if (this.selectedFormat && !this.afficherListeModeles) {
              this.loadMappingsForSelected();
            } else if (!this.selectedFormat) {
              this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
            }
            this.toastr.success('Format supprimé.', 'Export');
          });
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Export');
        }
      });
  }

  private loadMappingsForSelected(): void {
    if (!this.selectedFormat?.id) {
      this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
      return;
    }
    const formatId = Number(this.selectedFormat.id);
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
      return;
    }
    this.mappingLoading = true;
    this.api
      .getDataExportMappings(formatId, stationId, userId)
      .pipe(finalize(() => (this.mappingLoading = false)))
      .subscribe({
        next: (rows) => {
          if (rows.length) {
            const sorted = [...rows].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
            this.mapping = sorted.map((r) => ({
              id: r.id,
              colonneFichier: r.colonne_fichier,
              champCible: r.champ_source,
              transformation: r.transformation ?? ''
            }));
          } else {
            this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
          }
        },
        error: () => {
          this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
          this.toastr.warning('Impossible de charger le mapping export.', 'Export');
        }
      });
  }

  ajouterLigneMapping(): void {
    if (this.mappingLoading || this.savingMapping) {
      return;
    }
    this.mapping.push({ colonneFichier: '', champCible: '', transformation: '' });
  }

  supprimerLigneMapping(index: number): void {
    if (index < 0 || index >= this.mapping.length || this.mapping.length <= 1) {
      return;
    }
    const row = this.mapping[index];
    const formatId = Number(this.selectedFormat?.id);
    const { stationId, userId } = getParametrageSessionIds();

    if (row.id != null && row.id > 0 && Number.isFinite(formatId) && formatId > 0 && stationId > 0 && userId > 0) {
      this.deletingRowIndex = index;
      this.api
        .deleteDataExportMapping(formatId, row.id, stationId, userId)
        .pipe(finalize(() => (this.deletingRowIndex = null)))
        .subscribe({
          next: () => {
            this.mapping.splice(index, 1);
            this.toastr.success('Ligne supprimée.', 'Export');
          },
          error: (err: unknown) => {
            this.toastr.error(this.httpErrorMessage(err), 'Export');
          }
        });
    } else {
      this.mapping.splice(index, 1);
    }
  }

  openNouveauFormatModal(): void {
    this.nouveauFormatNom = '';
    this.nouveauFormatType = '';
    this.modal.open(this.nouveauFormatModalTpl, {
      centered: true,
      backdrop: 'static',
      size: 'md'
    });
  }

  enregistrerNouveauFormat(modal: NgbActiveModal): void {
    const nom = (this.nouveauFormatNom || '').trim();
    const type = (this.nouveauFormatType || '').trim();
    if (!nom) {
      this.toastr.warning('Indiquez un nom pour le format.', 'Export');
      return;
    }
    if (!type) {
      this.toastr.warning('Indiquez un type pour le format.', 'Export');
      return;
    }
    const ref = this.state.getModuleRef(this.moduleKey);
    const { stationId, userId } = getParametrageSessionIds();
    if (!ref || stationId <= 0 || userId <= 0) {
      this.toastr.error('Module ou session invalide.', 'Export');
      return;
    }
    const tableDbId = resolveRefModuleTableDbId(ref);
    if (!Number.isFinite(tableDbId) || tableDbId <= 0) {
      this.toastr.error('Identifiant table (ref_data_module) invalide.', 'Export');
      return;
    }
    this.creatingFormat = true;
    this.api
      .postDataExportFormat(
        {
          station_id: stationId,
          table_db_id: tableDbId,
          nom,
          type,
          config_json: {},
          actif: true
        },
        stationId,
        userId
      )
      .pipe(finalize(() => (this.creatingFormat = false)))
      .subscribe({
        next: () => {
          modal.close();
          this.state.reloadExportFormats(this.moduleKey).subscribe(() => {
            this.formats = this.state.getExportFormats(this.moduleKey);
            this.selectedFormat = this.formats[this.formats.length - 1] ?? null;
            this.afficherListeModeles = false;
            this.testResult = '';
            this.loadMappingsForSelected();
            this.toastr.success('Format créé.', 'Succès');
          });
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Export');
        }
      });
  }

  /**
   * Enregistrement unique : POST chaque ligne sans id (création), puis PUT de tout le mapping.
   */
  saveMapping(): void {
    if (!this.selectedFormat?.id) {
      this.toastr.warning('Sélectionnez un format d’export.', 'Export');
      return;
    }
    const formatId = Number(this.selectedFormat.id);
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.warning('Session invalide : station ou utilisateur introuvable.', 'Export');
      return;
    }
    for (let i = 0; i < this.mapping.length; i++) {
      const m = this.mapping[i];
      if (!m.champCible?.trim() || !m.colonneFichier?.trim()) {
        this.toastr.warning(`Ligne ${i + 1} : renseignez le champ source et la colonne fichier.`, 'Export');
        return;
      }
    }

    const sansId = this.mapping.map((m, i) => ({ m, i })).filter(({ m }) => m.id == null);

    const rowsPayload = (): DataExportMappingWriteDto[] =>
      this.mapping.map((m, ordre) => ({
        champ_source: m.champCible.trim(),
        colonne_fichier: m.colonneFichier.trim(),
        transformation: m.transformation?.trim() ? m.transformation.trim() : null,
        ordre
      }));

    const apresPosts$ = (
      sansId.length > 0
        ? from(sansId).pipe(
            switchMap(({ m, i }) => {
              const body: DataExportMappingWriteDto = {
                champ_source: m.champCible.trim(),
                colonne_fichier: m.colonneFichier.trim(),
                transformation: m.transformation?.trim() ? m.transformation.trim() : null,
                ordre: i
              };
              return this.api.postDataExportMapping(formatId, stationId, userId, body).pipe(
                tap((dto) => {
                  if (dto?.id != null) {
                    this.mapping[i].id = dto.id;
                  }
                })
              );
            }),
            last()
          )
        : of(null)
    ) as any;

    this.savingMapping = true;
    apresPosts$
      .pipe(
        switchMap(() => this.api.putDataExportMappings(formatId, stationId, userId, rowsPayload())),
        finalize(() => (this.savingMapping = false))
      )
      .subscribe(
        () => {
          this.toastr.success('Mapping export enregistré.', 'Export');
          this.loadMappingsForSelected();
        },
        (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Export');
        }
      );
  }

  testExport(): void {
    const fmt = this.selectedFormat;
    const formatId = Number(fmt?.id);
    if (!fmt || !Number.isFinite(formatId) || formatId <= 0) {
      this.toastr.warning(
        'Aucun format d’export disponible pour ce module (liste vide ou id invalide côté API).',
        'Export'
      );
      return;
    }
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.warning('Session invalide : station ou utilisateur introuvable.', 'Export');
      return;
    }
    this.testing = true;
    this.testResult = '';
    this.api
      .postExportTest(formatId, stationId, userId, { destination: this.destination })
      .pipe(finalize(() => (this.testing = false)))
      .subscribe({
        next: () => {
          this.toastr.success('Test d’export envoyé.', 'Export');
          this.testResult = `OK — format « ${fmt.nom} » (#${formatId}), destination : ${this.destination}.`;
        },
        error: (err: unknown) => {
          const msg = this.httpErrorMessage(err);
          this.toastr.error(msg, 'Export');
          this.testResult = msg;
        }
      });
  }

  private httpErrorMessage(err: unknown): string {
    const e = err as { error?: unknown; message?: string; status?: number };
    if (e?.status === 0) {
      return 'Serveur injoignable (réseau, CORS ou URL paramétrage).';
    }
    const b = e?.error;
    if (typeof b === 'string' && b.trim()) {
      return b;
    }
    if (b && typeof b === 'object') {
      const o = b as { message?: string; error?: string; details?: string };
      return o.message || o.error || o.details || e?.message || 'La requête a échoué.';
    }
    return e?.message || 'La requête a échoué.';
  }
}
